import { reaction } from 'mobx';

export const CONTRAST_QUERY = '(prefers-contrast: more)';
export const FORCED_COLORS_QUERY = '(forced-colors: active)';

export function normalizeContrastPreference(value) {
    return ['standard', 'medium', 'high'].includes(value) ? value : 'system';
}

export function isDyrejournalEnabled(appState = {}) {
    return !!(appState.currentCustomer?.menuItems?.dyrejournal || appState.opts?.showDyrejournal);
}

export function resolveContrastPreference(value, prefersContrast = false, forcedColors = false) {
    // The browser owns the palette in forced colors, including when Standard is selected.
    if (forcedColors) return 'forced';
    const preference = normalizeContrastPreference(value);
    if (preference !== 'system') return preference;
    return prefersContrast ? 'high' : 'standard';
}

function getMediaQuery(matchMedia, query) {
    try {
        return typeof matchMedia === 'function' ? matchMedia(query) : null;
    } catch (err) {
        return null;
    }
}

// Observe preferences as well as product/user changes. This also catches edits made in an open profile drawer.
export function watchContrastPreference({ appState, userStore, document, matchMedia } = {}) {
    const contrastMedia = getMediaQuery(matchMedia, CONTRAST_QUERY);
    const forcedMedia = getMediaQuery(matchMedia, FORCED_COLORS_QUERY);
    const roots = [document?.documentElement, document?.body].filter(Boolean);
    const readPreference = () => ({
        enabled: !!userStore?.user?.id && isDyrejournalEnabled(appState),
        preference: userStore?.user?.settings?.dyrejournalContrast,
    });
    const apply = ({ enabled, preference }) => {
        const mode = resolveContrastPreference(preference, !!contrastMedia?.matches, !!forcedMedia?.matches);
        roots.forEach(root => {
            if (enabled) {
                root.setAttribute('data-dyrejournal', 'true');
                root.setAttribute('data-dyrejournal-contrast', mode);
            } else {
                root.removeAttribute('data-dyrejournal');
                root.removeAttribute('data-dyrejournal-contrast');
            }
        });
    };
    const stopReaction = reaction(readPreference, apply, { fireImmediately: true });
    const onMediaChange = () => apply(readPreference());
    const queries = [contrastMedia, forcedMedia].filter(Boolean);
    queries.forEach(media => {
        if (media.addEventListener) media.addEventListener('change', onMediaChange);
        else if (media.addListener) media.addListener(onMediaChange);
    });
    return () => {
        stopReaction();
        queries.forEach(media => {
            if (media.removeEventListener) media.removeEventListener('change', onMediaChange);
            else if (media.removeListener) media.removeListener(onMediaChange);
        });
        roots.forEach(root => {
            root.removeAttribute('data-dyrejournal');
            root.removeAttribute('data-dyrejournal-contrast');
        });
    };
}
